import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import FormContainer from '../components/FormContainer';
import Meta from '../components/Meta';
import { useLoginMutation } from '../slices/usersApiSlice';
import { setCredentials } from '../slices/authSlice';
import { auth } from '../firebase';
import { signInWithPhoneNumber } from 'firebase/auth';
import { setupRecaptchaVerifier, clearRecaptcha } from '../hooks/useRecaptchaVerifier';
import { toast } from 'react-toastify';
import initFirebaseAppCheck from '../utils/initFirebaseAppCheck';
import TermsModal from '../components/TermsModal';

const PhoneLoginScreen = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const confirmationResultRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  const { userInfo } = useSelector((state) => state.auth);

  const { search } = useLocation();
  const redirect = new URLSearchParams(search).get('redirect') || '/';

  const [showTerms, setShowTerms] = useState(false);

  if (process.env.NODE_ENV !== 'development'){
    initFirebaseAppCheck();
  }

  // Redirect if already logged in
  useEffect(() => {
    if (userInfo) {
      navigate(redirect);
    }
  }, [userInfo, redirect, navigate]);

  // Cleanup reCAPTCHA when screen unmounts
  useEffect(() => {
    return () => {
      clearRecaptcha();
      confirmationResultRef.current = null;
    };
  }, []);

  const onSignInSubmit = async () => {
    const containerId = 'recaptcha-container';
    const formattedPhone = phone.startsWith('+') ? phone : `+91${phone}`;

    try {
      setSendingOtp(true); // <-- start loader for Send OTP button
      
      await new Promise((res) => requestAnimationFrame(res));
      // Step 1: Setup reCAPTCHA only when needed
      const verifier = await setupRecaptchaVerifier(auth, containerId);

      // Step 2: Request OTP
      const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, verifier);
      confirmationResultRef.current = confirmationResult;

      //alert('OTP has been sent!');
      toast.success("OTP has been sent", { autoClose: 1000 });
    } catch (error) {
      console.error('Failed to send OTP:', error);
      toast.error('Could not send OTP. Please try again', { autoClose: 2000 });
      setTimeout(() => window.location.reload(), 2000);
    } finally {
      setSendingOtp(false); // <-- stop loader
    }
  };

  const verifyOtp = async () => {
    try {
      setVerifyingOtp(true); // <-- start loader for Verify OTP button
      // Step 1: Ensure confirmationResult exists
      const confirmationResult = confirmationResultRef.current;
      if (!confirmationResult) {
        alert('OTP session expired or not initialized. Please resend OTP.');
        return;
      }

      // Step 2: Confirm the OTP
      const result = await confirmationResult.confirm(otp);
      const token = await result.user.getIdToken(true);

      // Step 3: Send Firebase token to backend to establish session
      const res = await login(token).unwrap();
      dispatch(setCredentials({ ...res }));
      navigate(redirect);
    } catch (error) {
      console.error('OTP verification or login failed:', error);
      toast.error('Invalid OTP. Please try again.', { autoClose: 2000 });
    } finally {
      setVerifyingOtp(false); // <-- stop loader
    }
  };

  return (
    <FormContainer>
      <Meta/>
      <h2>Phone OTP Sign In</h2>
      <Form autoComplete='off'>
        <Form.Group controlId='phone' className='my-3'>
          <Form.Label>
            <strong>Phone <span className='text-danger'>*</span></strong>
          </Form.Label>
          <Row>
            <Col>
              <Form.Control
                type='tel'
                placeholder='10 digit India phone number'
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </Col>
            <Col>
              <Button onClick={onSignInSubmit} disabled={isLoading || !phone || sendingOtp}>
                {sendingOtp ? (
                  <>
                    <Spinner animation="border" size="sm" /> Sending...
                  </>
                ) : (
                  'Send OTP'
                )}
              </Button>
            </Col>
          </Row>
        </Form.Group>
        <Form.Group>
          <Form.Label>
            <strong><span className='text-danger'>Make sure to check for OTP in your Spam & Blocked SMS as well</span></strong>
          </Form.Label>
        </Form.Group>
        <Form.Group controlId='otp' className='my-3'>
          <Form.Label>
            <strong>OTP <span className='text-danger'>*</span></strong>
          </Form.Label>
          <Row>
            <Col>
              <Form.Control
                type='number'
                placeholder='Enter OTP'
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </Col>
            <Col>
              <Button onClick={verifyOtp} disabled={isLoading || !otp || verifyingOtp}>
                {verifyingOtp ? (
                  <>
                    <Spinner animation="border" size="sm" /> Verifying...
                  </>
                ) : (
                  'Verify OTP'
                )}
              </Button>
            </Col>
          </Row>
        </Form.Group>
      </Form>

      <div id='recaptcha-container' />
      
      <div className="mt-2">
        <p>
          By continuing to use the website, you agree to our{" "}
          <span
            className="terms-text-link"
            onClick={() => setShowTerms(true)}
            role="button"
            tabIndex={0}
          >
            Terms & Conditions
          </span>
        </p>
      </div>
      <TermsModal show={showTerms} onHide={() => setShowTerms(false)} />
      <Row className='mt-2'>
        <Col>
          New Customer?{' '}
          <Link to={redirect ? `/register?redirect=${redirect}` : '/register'}>Register</Link>
        </Col>
      </Row>
    </FormContainer>
  );
};

export default PhoneLoginScreen;